import { useState, useRef, useMemo } from 'react';
import {
  Box, Typography, TextField, Autocomplete, Chip, Tooltip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import { Refresh, Visibility } from '@mui/icons-material';
import {
  DataGridPremium, GridColDef, GridRenderCellParams, GridPaginationModel,
} from '@mui/x-data-grid-premium';
import { useQuery } from '@tanstack/react-query';
import { startOfDay, subDays } from 'date-fns';
import { auditService, AuditRow } from '../../services/audit.service';
import { useUrlState, codecs } from '../../hooks/useUrlState';
import { formatInOffset, DEFAULT_TZ_OFFSET, dayBounds } from '../../utils/timezone';
import { useGridHeight } from '../../hooks/useGridHeight';
import DateRangePicker from '../../components/DateRangePicker';
import TimezoneSelect from '../../components/TimezoneSelect';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtDate(d: Date): string {
  // Use local date parts to avoid timezone shift (toISOString converts to UTC)
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getToday() { return startOfDay(new Date()); }

const ACTION_OPTIONS = [
  { value: '', label: 'All actions' },
  { value: 'create', label: 'Create' },
  { value: 'update', label: 'Update' },
  { value: 'archive', label: 'Archive' },
  { value: 'unarchive', label: 'Unarchive' },
  { value: 'delete', label: 'Delete' },
];

const ALL_ENTITY = { value: '', label: 'All entities' };

function userLabel(row: AuditRow): string {
  const name = `${row.user_first_name || ''} ${row.user_last_name || ''}`.trim();
  const user = name || row.user_email || row.user_id;
  if (row.api_key_id) {
    const key = `🔑 ${row.api_key_name || 'API key'}`;
    return user ? `${key} (${user})` : key;
  }
  // An MCP tool acts as the user (no api key), so flag the channel + which client, or it reads as a UI edit.
  if (row.source === 'mcp') {
    const via = row.mcp_client_name ? `MCP · ${row.mcp_client_name}` : 'MCP';
    return user ? `${user} · ${via}` : via;
  }
  return user || 'system';
}

function actionColor(action: string): 'success' | 'info' | 'warning' | 'error' | 'default' {
  switch (action) {
    case 'create': return 'success';
    case 'update': return 'info';
    case 'archive': return 'warning';
    case 'delete': return 'error';
    default: return 'default';
  }
}

// Keys whose value changed between old_values and new_values.
function changedKeys(row: AuditRow): string[] {
  if (row.action === 'delete') return [];
  const oldV = row.old_values || {};
  const newV = row.new_values || {};
  if (row.action === 'create') return Object.keys(newV);
  const keys = new Set([...Object.keys(oldV), ...Object.keys(newV)]);
  return Array.from(keys).filter((k) => JSON.stringify(oldV[k]) !== JSON.stringify(newV[k]));
}

function changesSummary(row: AuditRow): string {
  if (row.action === 'delete') return '(deleted)';
  return changedKeys(row).join(', ');
}

function renderValue(v: any): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AuditLogPage() {
  // URL-driven so the page can be deep-linked to one record ("who changed THIS
  // traffic source") and the filters survive a refresh.
  const [dateRange, setDateRange] = useUrlState<[Date | null, Date | null]>(
    'dates', [subDays(getToday(), 6), getToday()], codecs.dateRange,
  );
  const [entityType, setEntityType] = useUrlState('entity_type', '', codecs.str);
  const [entityId, setEntityId] = useUrlState('entity_id', '', codecs.str);
  const [action, setAction] = useUrlState('action', '', codecs.str);
  const [pagination, setPagination] = useUrlState('page', { page: 0, pageSize: 100 }, codecs.pageModel);
  const { page, pageSize } = pagination;
  const setPage = (p: number) => setPagination({ page: p, pageSize });
  const setPageSize = (ps: number) => setPagination({ page: 0, pageSize: ps });
  const [timezone, setTimezone] = useUrlState('tz', DEFAULT_TZ_OFFSET, codecs.num);
  const [detailRow, setDetailRow] = useState<AuditRow | null>(null);

  const dateFrom = dateRange[0] ? fmtDate(dateRange[0]) : fmtDate(new Date());
  const dateTo = dateRange[1] ? fmtDate(dateRange[1]) : fmtDate(new Date());

  const { data: entityTypes } = useQuery({
    queryKey: ['audit-entity-types'],
    queryFn: () => auditService.entityTypes(),
  });

  const entityOptions = useMemo(
    () => [ALL_ENTITY, ...(entityTypes || []).map((t) => ({ value: t, label: t }))],
    [entityTypes],
  );

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['audit-list', dateFrom, dateTo, entityType, entityId, action, page, pageSize],
    queryFn: () => auditService.query({
      ...(entityType && { entity_type: entityType }),
      ...(entityId && { entity_id: entityId }),
      ...(action && { action }),
      from: dayBounds(dateFrom, dateTo).date_from,
      to: dayBounds(dateFrom, dateTo).date_to,
      limit: pageSize,
      offset: page * pageSize,
    }),
  });

  const filterRef = useRef<HTMLDivElement>(null);
  const gridHeight = useGridHeight(filterRef);

  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'created_at', headerName: 'Time', width: 160,
      valueFormatter: (v) => formatInOffset(v as string, timezone),
    },
    {
      field: 'user', headerName: 'User', width: 180, sortable: false,
      valueGetter: (_v, row: AuditRow) => userLabel(row),
    },
    {
      field: 'action', headerName: 'Action', width: 110,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip label={params.value} size="small" variant="filled"
            color={actionColor(params.value)} sx={{ fontWeight: 500, minWidth: 72 }} />
        </Box>
      ),
    },
    { field: 'entity_type', headerName: 'Entity', width: 140 },
    {
      field: 'entity_id', headerName: 'Entity ID', width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Tooltip title={params.value ? `${params.value} — click to show only this record` : ''}>
            <Typography
              variant="caption"
              onClick={(e) => {
                e.stopPropagation();
                if (!params.value) return;
                setEntityType(params.row.entity_type || '');
                setEntityId(String(params.value));
              }}
              sx={{
                fontFamily: 'monospace', fontSize: '0.7rem', cursor: params.value ? 'pointer' : 'default',
                '&:hover': { textDecoration: params.value ? 'underline' : 'none' },
              }}
              noWrap
            >
              {params.value}
            </Typography>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'changes', headerName: 'Changed fields', flex: 1, sortable: false,
      valueGetter: (_v, row: AuditRow) => changesSummary(row),
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Tooltip title={params.value || ''}>
            <Typography variant="body2" noWrap>{params.value}</Typography>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'details', headerName: '', width: 80, sortable: false, filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Tooltip title="Details">
            <IconButton size="small" onClick={() => setDetailRow(params.row as AuditRow)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ], [timezone]);

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>Audit Log</Typography>

      {/* Filters row */}
      <Box ref={filterRef} sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Autocomplete size="small" sx={{ width: 200 }}
          options={entityOptions} getOptionLabel={(o) => o.label}
          value={entityOptions.find(e => e.value === entityType) || entityOptions[0]}
          onChange={(_, v) => setEntityType(v?.value || '')}
          disableClearable
          isOptionEqualToValue={(o, v) => o.value === v.value}
          renderInput={(params) => <TextField {...params} label="Entity type" />}
        />
        <Autocomplete size="small" sx={{ width: 170 }}
          options={ACTION_OPTIONS} getOptionLabel={(o) => o.label}
          value={ACTION_OPTIONS.find(a => a.value === action) || ACTION_OPTIONS[0]}
          onChange={(_, v) => setAction(v?.value || '')}
          disableClearable
          isOptionEqualToValue={(o, v) => o.value === v.value}
          renderInput={(params) => <TextField {...params} label="Action" />}
        />

        {entityId && (
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={`Record: ${entityId.slice(0, 8)}…`}
            onDelete={() => setEntityId('')}
            sx={{ fontFamily: 'monospace' }}
          />
        )}

        <Box sx={{ flex: 1 }} />

        <TimezoneSelect value={timezone} onChange={setTimezone} />
        <DateRangePicker value={dateRange} onChange={setDateRange} />

        <Tooltip title="Refresh">
          <IconButton size="small" onClick={() => refetch()}>
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Data Grid */}
      <DataGridPremium
        rows={data?.data || []}
        getRowId={(row: AuditRow) => row.id}
        columns={columns}
        loading={isLoading}
        density="compact"
        rowCount={data?.total || 0}
        pagination
        paginationMode="server"
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(model: GridPaginationModel) => { setPage(model.page); setPageSize(model.pageSize); }}
        pageSizeOptions={[50, 100, 200, 400]}
        disableRowSelectionOnClick
        sx={{
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          height: gridHeight,
          '& .MuiDataGrid-row:nth-of-type(even)': {
            bgcolor: 'action.hover',
          },
          '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
        }}
      />

      <AuditDetailsDialog row={detailRow} timezone={timezone} onClose={() => setDetailRow(null)} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Details Dialog
// ---------------------------------------------------------------------------

function AuditDetailsDialog({ row, timezone, onClose }: { row: AuditRow | null; timezone: number; onClose: () => void }) {
  const keys = row ? changedKeys(row) : [];
  const oldV = row?.old_values || {};
  const newV = row?.new_values || {};

  return (
    <Dialog open={!!row} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Audit Details</DialogTitle>
      <DialogContent>
        {row && (
          <>
            <Table size="small" sx={{ mb: 2 }}>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: 140, border: 0 }}>Time</TableCell>
                  <TableCell sx={{ border: 0 }}>{formatInOffset(row.created_at, timezone)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, border: 0 }}>User</TableCell>
                  <TableCell sx={{ border: 0 }}>{userLabel(row)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, border: 0 }}>Action</TableCell>
                  <TableCell sx={{ border: 0 }}>
                    <Chip label={row.action} size="small" variant="filled" color={actionColor(row.action)} sx={{ fontWeight: 500 }} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, border: 0 }}>Entity</TableCell>
                  <TableCell sx={{ border: 0 }}>
                    {row.entity_type}
                    {' '}
                    <Typography component="span" variant="caption" sx={{ fontFamily: 'monospace' }}>{row.entity_id}</Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, border: 0 }}>IP Address</TableCell>
                  <TableCell sx={{ border: 0 }}>{row.ip_address || '-'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, border: 0, verticalAlign: 'top' }}>User Agent</TableCell>
                  <TableCell sx={{ border: 0, wordBreak: 'break-all' }}>{row.user_agent || '-'}</TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>Changes</Typography>
            {row.action === 'delete' ? (
              <Typography variant="body2" color="text.secondary">(record deleted)</Typography>
            ) : keys.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No field changes.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Field</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Old</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>New</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {keys.map((k) => (
                    <TableRow key={k}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{k}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all', color: 'text.secondary' }}>
                        {renderValue(oldV[k])}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                        {renderValue(newV[k])}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
